"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.sonarHttpDelete = sonarHttpDelete;
exports.sonarHttpGet = sonarHttpGet;
exports.sonarHttpGetBlob = sonarHttpGetBlob;
exports.sonarHttpGetStream = sonarHttpGetStream;
exports.sonarHttpPatch = sonarHttpPatch;
exports.sonarHttpPost = sonarHttpPost;
exports.sonarHttpPut = sonarHttpPut;
const dayjs_1 = __importDefault(require("dayjs"));
const _types_1 = require("../@types/index.js");
const authTokenLocalStoragKey = 'authToken';
function getAuthToken() {
    const authTokenStr = localStorage.getItem(authTokenLocalStoragKey);
    if (!authTokenStr) {
        return;
    }
    const authTokenObj = JSON.parse(authTokenStr);
    const { token } = authTokenObj;
    return token;
}
function setAuthToken(token) {
    localStorage.setItem(authTokenLocalStoragKey, JSON.stringify({ token }));
}
function withAuthHeader(authToken) {
    if (authToken) {
        return {
            [_types_1.HttpHeader.Authorization]: `Bearer ${authToken}`
        };
    }
    return null;
}
function withAuthRefreshHeader(authToken, skip) {
    if (authToken && !skip) {
        return {
            [_types_1.HttpHeader.RefreshToken]: 'true'
        };
    }
    return null;
}
function withSubdomainHeader(subdomain) {
    if (subdomain) {
        return {
            [_types_1.HttpHeader.Subdomain]: subdomain
        };
    }
    return null;
}
function withTimezoneHeader() {
    return {
        [_types_1.HttpHeader.Timezone]: (0, dayjs_1.default)()
            .utcOffset()
            .toString()
    };
}
function parseResponseHeaders(headers) {
    var _a;
    if (headers.has(_types_1.HttpHeader.Authorization)) {
        const value = ((_a = headers.get(_types_1.HttpHeader.Authorization)) !== null && _a !== void 0 ? _a : '').replace('Bearer ', '');
        if (value) {
            setAuthToken(value);
        }
    }
}
function sonarFetch(options) {
    const { body, endpoint, headers, method, skipAuthRefresh, skipSonarHeaders, subdomain } = options;
    const authToken = getAuthToken();
    const sonarHeaders = skipSonarHeaders ? {} : Object.assign(Object.assign(Object.assign(Object.assign({}, withAuthHeader(authToken)), withAuthRefreshHeader(authToken, skipAuthRefresh)), withSubdomainHeader(subdomain)), withTimezoneHeader());
    const requestHeaders = Object.assign(Object.assign({ Accept: 'application/json' }, headers), sonarHeaders);
    let fetchPromise;
    if (body) {
        const { file, fileOptions, multipart } = body;
        if (file || fileOptions || multipart) {
            const formData = new FormData();
            if (file) {
                formData.append('file', file);
            }
            if (fileOptions) {
                formData.append(fileOptions.formName, fileOptions.file);
            }
            fetchPromise = fetch(endpoint, {
                body: formData,
                headers: requestHeaders,
                method
            });
        }
        else {
            fetchPromise = fetch(endpoint, {
                body: JSON.stringify(body),
                headers: Object.assign(Object.assign({}, requestHeaders), { [_types_1.HttpHeader.ContentType]: 'application/json' }),
                method
            });
        }
    }
    else {
        fetchPromise = fetch(endpoint, {
            headers: requestHeaders,
            method
        });
    }
    return fetchPromise.then((response) => {
        parseResponseHeaders(response.headers);
        return response;
    });
}
/*
    To support primitive strings returns from API endpoints, need to verify if data content is a JSON value
    or a primitive string value. JSON.parse(...) throws an exception attempting to parse non-json strings.
    https://github.com/github/fetch/issues/268#issuecomment-293522869
*/
function isJSON(input) {
    const rxOne = /^[\],:{}\s]*$/u;
    const rxTwo = /\\(?:["\\/bfnrt]|u[0-9a-fA-F]{4})/gu;
    const rxThree = /"[^"\\\n\r]*"|true|false|null|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?/gu;
    const rxFour = /(?:^|:|,)(?:\s*\[)+/gu;
    return Boolean(input.length) && rxOne.test(input.replace(rxTwo, '@').replace(rxThree, ']')
        .replace(rxFour, ''));
}
function getResponsePromiseWithData(response) {
    return __awaiter(this, void 0, void 0, function* () {
        const { headers, status } = response;
        const result = {
            headers,
            response,
            status
        };
        if (status === _types_1.SuccessStatusCode.Ok || status === _types_1.SuccessStatusCode.Accepted) {
            const rawTextPromise = response.text();
            const rawText = yield rawTextPromise;
            const data = isJSON(rawText) ? JSON.parse(rawText) : rawText;
            return Object.assign({ data: data }, result);
        }
        return Promise.resolve(result);
    });
}
function getResponsePromiseWithBlobData(response) {
    return __awaiter(this, void 0, void 0, function* () {
        const { headers, status } = response;
        const result = {
            headers,
            response,
            status
        };
        if (status === _types_1.SuccessStatusCode.Ok) {
            const data = yield response.blob();
            return Object.assign({ data }, result);
        }
        return Promise.resolve(result);
    });
}
function getResponseWithStreamData(response) {
    const { headers, status } = response;
    const result = {
        headers,
        response,
        status
    };
    if (status === _types_1.SuccessStatusCode.Ok) {
        const data = response.body.getReader();
        return Object.assign({ data }, result);
    }
    return result;
}
function getFetchOptions(options) {
    const api = localStorage.getItem('api');
    const { headers, requestBody, skipAuthRefresh, skipSonarHeaders, subdomain, targetUri, uriType } = options;
    const endpoint = uriType === 'absolute' ? targetUri : `${api}${targetUri}`;
    return {
        body: requestBody,
        endpoint,
        headers,
        method: 'GET',
        skipAuthRefresh,
        skipSonarHeaders,
        subdomain
    };
}
function sonarHttpDelete(options) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield sonarFetch(Object.assign(Object.assign({}, getFetchOptions(options)), { method: 'DELETE' }));
        return getResponsePromiseWithData(response);
    });
}
function sonarHttpGet(options) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield sonarFetch(Object.assign(Object.assign({}, getFetchOptions(options)), { body: undefined, method: 'GET' }));
        return getResponsePromiseWithData(response);
    });
}
function sonarHttpGetBlob(options) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield sonarFetch(Object.assign(Object.assign({}, getFetchOptions(options)), { body: undefined, method: 'GET' }));
        return getResponsePromiseWithBlobData(response);
    });
}
function sonarHttpGetStream(options) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield sonarFetch(Object.assign(Object.assign({}, getFetchOptions(options)), { body: undefined, headers: {
                [_types_1.HttpHeader.Accept]: 'text/event-stream'
            }, method: 'GET' }));
        return getResponseWithStreamData(response);
    });
}
function sonarHttpPatch(options) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield sonarFetch(Object.assign(Object.assign({}, getFetchOptions(options)), { method: 'PATCH' }));
        return getResponsePromiseWithData(response);
    });
}
function sonarHttpPost(options) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield sonarFetch(Object.assign(Object.assign({}, getFetchOptions(options)), { method: 'POST' }));
        return getResponsePromiseWithData(response);
    });
}
function sonarHttpPut(options) {
    return __awaiter(this, void 0, void 0, function* () {
        const response = yield sonarFetch(Object.assign(Object.assign({}, getFetchOptions(options)), { method: 'PUT' }));
        return getResponsePromiseWithData(response);
    });
}
//# sourceMappingURL=sonarFetch.js.map